/**
 * Extract the Drive file ID from any of the common Drive URL formats:
 *   • https://drive.google.com/file/d/FILE_ID/view
 *   • https://drive.google.com/open?id=FILE_ID
 *   • https://lh3.googleusercontent.com/d/FILE_ID=w1000
 *
 * Returns null if no ID can be found.
 */
export function extractDriveFileId(url?: string | null): string | null {
  if (!url) return null;
  // /d/FILE_ID/ pattern — used in webViewLink and lh3 URLs
  const slashMatch = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
  if (slashMatch?.[1]) return slashMatch[1];
  // ?id=FILE_ID or &id=FILE_ID query-string pattern
  const queryMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  return queryMatch?.[1] ?? null;
}

/**
 * Convert any Drive URL into a direct-embed lh3 image URL (best for avatars).
 * Falls back to the original URL if it isn't a Drive URL or the ID can't be parsed.
 */
export function formatDriveToImageSrc(url?: string): string | undefined {
  if (!url) return undefined;
  if (!url.includes('drive.google.com') && !url.includes('googleusercontent.com')) return url;
  const fileId = extractDriveFileId(url);
  if (fileId) return `https://lh3.googleusercontent.com/d/${fileId}=w1000`;
  return url;
}

/**
 * Convert any Drive URL into a Drive-generated thumbnail (works for images AND PDFs).
 * Use this instead of formatDriveToImageSrc when the file may be a PDF.
 *
 * Note: for *video* file IDs this endpoint redirects to the lh3 image CDN
 * which returns 404 — prefer the server-stored `thumbnail_url` (poster frame
 * extracted at upload time) when rendering videos.
 */
export function formatDriveToThumbnail(url?: string, sz = 'w400'): string | undefined {
  if (!url) return undefined;
  const fileId = extractDriveFileId(url);
  if (fileId) return `https://drive.google.com/thumbnail?id=${fileId}&sz=${sz}`;
  return url;
}

/**
 * Convert any Drive URL into an iframe-safe embed URL for Drive's built-in
 * media player. Suitable for video preview, PDF viewing, etc.
 */
export function formatDriveToEmbed(url?: string): string | undefined {
  if (!url) return undefined;
  const fileId = extractDriveFileId(url);
  if (fileId) return `https://drive.google.com/file/d/${fileId}/preview`;
  return url;
}
